import { useDisclosure } from "@mantine/hooks";
import { type ReactNode, useEffect, useMemo } from "react";
import { push } from "react-router-redux";
import { P, match } from "ts-pattern";
import { c, t } from "ttag";
import _ from "underscore";

import { SettingsSection } from "metabase/admin/components/SettingsSection";
import { SettingHeader } from "metabase/admin/settings/components/SettingHeader";
import {
  skipToken,
  useGetCollectionQuery,
  useListMetabotsQuery,
  useUpdateMetabotMutation,
} from "metabase/api";
import { useAdminSetting } from "metabase/api/utils/settings";
import { canonicalCollectionId } from "metabase/collections/utils";
import { LoadingAndErrorWrapper } from "metabase/common/components/LoadingAndErrorWrapper";
import { CollectionPickerModal } from "metabase/common/components/Pickers/CollectionPicker";
import { useSetting, useToast } from "metabase/common/hooks";
import { getIcon } from "metabase/lib/icon";
import { useDispatch } from "metabase/lib/redux";
import {
  FIXED_METABOT_ENTITY_IDS,
  FIXED_METABOT_IDS,
} from "metabase/metabot/constants";
import {
  PLUGIN_EMBEDDING_IFRAME_SDK,
  PLUGIN_EMBEDDING_SDK,
  PLUGIN_MODERATION,
} from "metabase/plugins";
import {
  Box,
  Button,
  Flex,
  Icon,
  Loader,
  Stack,
  Switch,
  Text,
} from "metabase/ui";
import type {
  Collection,
  CollectionEssentials,
  EnterpriseSettingKey,
  MetabotInfo,
} from "metabase-types/api";

import { MetabotPromptSuggestionPane } from "./MetabotAdminSuggestedPrompts";
import { useMetabotIdPath } from "./utils";

export function MetabotConfig() {
  const isConfigured = useSetting("llm-metabot-configured?");

  const metabotId = useMetabotIdPath() ?? FIXED_METABOT_IDS.DEFAULT;
  const { data, isLoading, error } = useListMetabotsQuery();

  const metabot = data?.items?.find((bot) => bot.id === metabotId);

  const dispatch = useDispatch();

  const hasEmbedding =
    PLUGIN_EMBEDDING_SDK.isEnabled() || PLUGIN_EMBEDDING_IFRAME_SDK.isEnabled();

  const metabots = useMemo(() => _.sortBy(data?.items ?? [], "id"), [data]);

  useEffect(() => {
    if (!isConfigured) {
      dispatch(push("/admin/metabot/setup"));
    }
  }, [isConfigured, dispatch]);

  useEffect(() => {
    if (metabotId === FIXED_METABOT_IDS.EMBEDDED && !hasEmbedding) {
      dispatch(push(`/admin/metabot/${FIXED_METABOT_IDS.DEFAULT}`));
    }
  }, [metabotId, hasEmbedding, dispatch]);

  useEffect(() => {
    if (!metabot && metabots.length) {
      dispatch(push(`/admin/metabot/${metabots[0]?.id}`));
    }
  }, [metabots, metabotId, dispatch, metabot, isLoading]);

  if (isLoading || !metabot || !isConfigured) {
    return (
      <LoadingAndErrorWrapper
        loading={isLoading || !data}
        error={match({ isLoading, error, metabot, isConfigured })
          .with(
            { isLoading: false, error: P.not(null), isConfigured: true },
            () => t`Error fetching Metabots`,
          )
          .with(
            { isLoading: false, metabot: undefined, isConfigured: true },
            () => t`Not found.`,
          )
          .otherwise(() => null)}
      />
    );
  }

  const isEmbedMetabot =
    metabot.entity_id === FIXED_METABOT_ENTITY_IDS.EMBEDDED;

  const enabledSettingKey: EnterpriseSettingKey = isEmbedMetabot
    ? "embedded-metabot-enabled?"
    : "metabot-enabled?";

  return (
    <SettingsSection key={metabotId}>
      <Box>
        <SettingHeader
          id="configure-metabot"
          title={c("{0} is the name of an AI assistant")
            .t`Configure ${metabot.name}`}
          description={c("{0} is the name of an AI assistant") // eslint-disable-next-line metabase/no-literal-metabase-strings -- admin ui
            .t`${metabot.name} is Metabase's AI agent. To help ${metabot.name} more easily find and focus on the data you care about most, configure what content it should be able to access or use to create queries.`}
        />
        {isEmbedMetabot && (
          <Text c="text-secondary" maw="40rem">
            {t`If you're embedding the Metabot component in an app, you can specify a different collection that embedded Metabot is allowed to use for creating queries.`}
          </Text>
        )}
        <MetabotEnabledToggle
          settingKey={enabledSettingKey}
          isEmbedMetabot={isEmbedMetabot}
        />
      </Box>

      <MetabotSettingsBody settingKey={enabledSettingKey}>
        <PLUGIN_MODERATION.MetabotVerifiedContentConfigurationPane
          metabot={metabot}
        />

        <MetabotCollectionConfigurationPane
          metabot={metabot}
          title={
            isEmbedMetabot
              ? undefined
              : t`Collection for natural language querying`
          }
        />

        <MetabotPromptSuggestionPane metabot={metabot} />
      </MetabotSettingsBody>
    </SettingsSection>
  );
}

function MetabotEnabledToggle({
  settingKey,
  isEmbedMetabot = false,
}: {
  settingKey: EnterpriseSettingKey;
  isEmbedMetabot?: boolean;
}) {
  const { value, updateSetting, isLoading } = useAdminSetting(settingKey);

  const handleToggle = async (checked: boolean) => {
    await updateSetting({ key: settingKey, value: checked });
  };

  return (
    <Box mt="lg">
      <SettingHeader
        id="enable-metabot"
        title={isEmbedMetabot ? t`Enable Embedded Metabot` : t`Enable Metabot`}
        description={
          isEmbedMetabot
            ? undefined
            : t`Metabot is Metabase's AI assistant. When enabled, Metabot will be available to help users create queries, analyze data, and answer questions about your data.` // eslint-disable-line metabase/no-literal-metabase-strings -- admin UI
        }
      />
      <Flex align="center" gap="md" mt="md">
        <Switch
          data-testid="metabot-enabled-toggle"
          checked={!!value}
          onChange={(e) => handleToggle(e.target.checked)}
          disabled={isLoading}
          w="auto"
          size="sm"
        />
        <Text c={value ? "text-primary" : "text-secondary"} fw="500">
          {isEmbedMetabot
            ? value
              ? t`Embedded Metabot is enabled`
              : t`Embedded Metabot is disabled`
            : value
              ? t`Metabot is enabled`
              : t`Metabot is disabled`}
        </Text>
      </Flex>
    </Box>
  );
}

function MetabotSettingsBody({
  settingKey,
  children,
}: {
  settingKey: EnterpriseSettingKey;
  children: ReactNode;
}) {
  const { value } = useAdminSetting(settingKey);
  const isDisabled = value === false;

  return (
    <Stack
      gap="lg"
      opacity={isDisabled ? 0.4 : 1}
      aria-disabled={isDisabled || undefined}
      style={{
        pointerEvents: isDisabled ? "none" : "auto",
        transition: "opacity 150ms ease",
      }}
    >
      {children}
    </Stack>
  );
}

function MetabotCollectionConfigurationPane({
  metabot,
  title,
}: {
  metabot: MetabotInfo;
  title?: string;
}) {
  const metabotId = metabot.id;
  const metabotName = metabot.name;

  const {
    data: collection,
    isLoading,
    error,
  } = useGetCollectionQuery({
    id: metabot.collection_id ?? "root",
  });

  const [updateMetabot, { isLoading: isUpdating }] = useUpdateMetabotMutation();
  const [isOpen, { open, close }] = useDisclosure(false);
  const [sendToast] = useToast();

  if (isLoading || !collection || error) {
    return (
      <LoadingAndErrorWrapper
        loading={isLoading}
        error={error ? t`Error fetching Metabot configuration` : null}
      />
    );
  }

  const handleUpdateCollectionId = async (
    newEntity: Pick<MetabotInfo, "id" | "name">,
  ) => {
    close();
    const result = await updateMetabot({
      id: metabotId,
      collection_id: canonicalCollectionId(newEntity.id),
    });

    if (result.error) {
      sendToast({
        message: t`Error setting ${newEntity.name}`,
        icon: "warning",
      });
    }
  };

  const defaultTitle = c("{0} is the name of an AI assistant")
    .t`Collection ${metabotName} can use`;

  return (
    <Box>
      <SettingHeader id="allow-metabot" title={title ?? defaultTitle} />
      <CollectionInfo collection={collection} />
      <Flex gap="md" mt="md">
        <Button onClick={open} leftSection={isUpdating && <Loader size="xs" />}>
          {match({ isMutating: isUpdating, collection })
            .with({ isMutating: true }, () => t`Updating collection...`)
            .with({ collection: undefined }, () => t`Pick a collection`)
            .otherwise(() => t`Pick a different collection`)}
        </Button>
      </Flex>
      {isOpen && (
        <CollectionPickerModal
          title={t`Select a collection`}
          value={{
            id: collection?.id ?? null,
            model: "collection",
          }}
          onChange={(item) =>
            handleUpdateCollectionId(item as Pick<MetabotInfo, "id" | "name">)
          }
          onClose={close}
          options={{
            hasRootCollection: true,
            hasPersonalCollections: false,
          }}
        />
      )}
    </Box>
  );
}

function CollectionInfo({ collection }: { collection: Collection }) {
  const { data: collectionInfo } = useGetCollectionQuery(
    collection?.id ? { id: collection.id } : skipToken,
  );

  if (!collection || !collectionInfo) {
    return null;
  }

  const parent = collectionInfo?.effective_ancestors?.slice(-1)?.[0];

  return (
    <Flex align="center" gap="sm" c="text-tertiary" mb="sm">
      {parent && (
        <>
          <CollectionDisplay collection={parent} />
          <Text c="text-tertiary" fw="bold">
            /
          </Text>
        </>
      )}
      <CollectionDisplay collection={collectionInfo} />
    </Flex>
  );
}

const CollectionDisplay = ({
  collection,
}: {
  collection: CollectionEssentials;
}) => {
  const icon = getIcon({ model: "collection", ...collection });
  return (
    <Flex align="center" gap="sm">
      <Icon {...icon} c={icon.color ?? "brand"} />
      <Text c="text-secondary" fw="bold">
        {collection.name}
      </Text>
    </Flex>
  );
};
